"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Printer, Download, Mail } from "lucide-react";
import { toggleBookmark, toBookmark, useIsBookmarked } from "@/lib/bookmarks";
import type { IFormationDetail } from "@/types/formation";
import DownloadProgramModal from "./DownloadProgramModal";
import ShareProgramModal from "./ShareProgramModal";

interface ProgramActionsProps {
  formation: IFormationDetail;
  /** Classes for the outer wrapper — controls positioning/visibility per usage
   *  (e.g. the mobile SummaryBar row). */
  className?: string;
}

/**
 * One definition for the four round actions. 
 */
const ACTION_BUTTON_CLASS =
  "inline-flex size-11.5 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#fc7f16] transition duration-200 hover:scale-110 z-10";

const ProgramActions: React.FC<ProgramActionsProps> = ({ formation, className = "" }) => {
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const isSaved = useIsBookmarked(formation.slug);

  return (
    <>
      <div className={className}>
        <div className="flex items-center gap-1">
          {/* Save to favourites */}
          <button
            type="button"
            onClick={() => toggleBookmark(toBookmark(formation))}
            aria-pressed={isSaved}
            aria-label={isSaved ? "Retirer des favoris" : "Ajouter aux favoris"}
            title={isSaved ? "Retirer des favoris" : "Ajouter aux favoris"}
            className={`${ACTION_BUTTON_CLASS} ${
              isSaved
                ? "bg-[#fc7f16] hover:bg-[#e6720f]"
                : "bg-white hover:bg-[#fff5e6]"
            }`}
          >
            <Image
              className={`size-4.5 ${isSaved ? "brightness-0 invert" : ""}`}
              src="/assets/icons/icon-bookmark.svg"
              alt="Favoris"
              title="Favoris"
              aria-hidden="true"
              width={25}
              height={25}
            />
          </button>

          {/* Printable programme */}
          <Link
            href={`/formation/${formation.slug}/programme`}
            aria-label="Voir le programme imprimable"
            title="Voir le programme"
            className={`${ACTION_BUTTON_CLASS} bg-white hover:bg-[#fff5e6]`}
          >
            <Printer size={20} className="text-[#fc7f16]" aria-hidden="true" />
          </Link>

          {/* Download programme (lead-capture modal) */}
          <button
            type="button"
            onClick={() => setIsDownloadOpen(true)}
            aria-label="Télécharger le programme"
            title="Télécharger le programme"
            className={`${ACTION_BUTTON_CLASS} bg-white hover:bg-[#fff5e6]`}
          >
            <Download size={20} className="text-[#fc7f16]" aria-hidden="true" />
          </button>

          {/* Share by e-mail */}
          <button
            type="button"
            onClick={() => setIsShareOpen(true)}
            aria-label="Partager le programme par mail"
            title="Partager le programme par mail"
            className={`${ACTION_BUTTON_CLASS} bg-white hover:bg-[#fff5e6]`}
          >
            <Mail size={20} className="text-[#fc7f16]" aria-hidden="true" />
          </button>
        </div>
      </div>

      <DownloadProgramModal
        isOpen={isDownloadOpen}
        onClose={() => setIsDownloadOpen(false)}
        programName={formation.title}
        formationSlug={formation.slug}
        programPdfUrl={formation.program_pdf_url}
      />

      <ShareProgramModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        formationSlug={formation.slug}
        programName={formation.title}
      />
    </>
  );
};

export default ProgramActions;
